import { useState, useEffect, useRef } from 'react';
import { X, Heart, Share2, MessageCircle, Play, Pause } from 'lucide-react';
import { Item, api } from '../lib/api';
import { formatPrice, t } from '../lib/i18n';
import { haptic, getTelegramWebApp } from '../lib/telegram';
import { useTimeOnCard } from '../hooks/useTimeOnCard';
import { detectMediaPreview } from '../lib/media';

interface ItemDetailModalProps {
  item: Item;
  onClose: () => void;
  onLike: () => void;
  isLiked: boolean;
  onMessageClick?: (userId: number, username?: string, forwardedItem?: Item) => void;
}

// Product-specific metadata configuration
const getProductMetadata = (item: Item) => {
  const category = item.category.toLowerCase();
  const hasAudioPreview = item.preview_url || item.media_url;
  
  switch (category) {
    case 'beat':
      // Only show BPM and tonality for beats with audio
      if (hasAudioPreview) {
        return {
          fields: [
            { key: 'bpm', label: 'BPM', value: item.bpm },
            { key: 'key', label: 'Тональність', value: item.key },
          ],
          showExclusive: true,
        };
      }
      // If no audio (beat pack/kit), show format info
      return {
        fields: [
          { key: 'format', label: 'Формат', value: typeof item.formats?.[0] === 'object' ? item.formats[0]?.id : item.formats?.[0] },
        ],
        showExclusive: false,
      };
    
    case 'cover':
      // For cover art - show creation method, style, format
      return {
        fields: [
          { key: 'style', label: 'Стиль', value: item.tags?.find(t => ['drawn', 'photoshop', 'digital', '3d', 'minimal', 'illustrative', 'realistic', 'abstract'].includes(t.toLowerCase())) },
          { key: 'method', label: 'Метод', value: item.tags?.find(t => ['ai', 'manual', 'hand-drawn', 'mixed'].includes(t.toLowerCase())) },
          { key: 'resolution', label: 'Якість', value: item.tags?.find(t => ['hd', '4k', 'print-ready', 'web'].includes(t.toLowerCase())) },
        ],
        showExclusive: false,
      };
    
    case 'feature':
      // For features - only show genre/style if it's a vocal/audio feature
      if (hasAudioPreview) {
        return {
          fields: [
            { key: 'genre', label: 'Жанр', value: item.tags?.[0] },
            { key: 'style', label: 'Стиль', value: item.tags?.[1] },
          ],
          showExclusive: true,
        };
      }
      // For non-audio features, show generic info
      return {
        fields: [
          { key: 'type', label: 'Тип', value: item.tags?.[0] },
        ],
        showExclusive: false,
      };
    
    case 'mix':
      // For mixing/mastering services
      return {
        fields: [
          { key: 'delivery', label: 'Термін', value: item.tags?.find(t => t.includes('day') || t.includes('днів')) },
          { key: 'format', label: 'Формат', value: typeof item.formats?.[0] === 'object' ? item.formats[0]?.id : item.formats?.[0] },
        ],
        showExclusive: false,
      };
    
    case 'kit':
      // For sample/loop kits
      return {
        fields: [
          { key: 'samples', label: 'Кількість', value: item.tags?.find(t => t.includes('samples') || t.includes('звуків')) },
          { key: 'format', label: 'Формат', value: typeof item.formats?.[0] === 'object' ? item.formats[0]?.id : item.formats?.[0] },
        ],
        showExclusive: false,
      };
    
    case 'service':
      // For services - show delivery time, revisions
      return {
        fields: [
          { key: 'delivery', label: 'Термін', value: item.tags?.find(t => t.includes('day') || t.includes('днів')) },
          { key: 'revisions', label: 'Правки', value: item.tags?.find(t => t.includes('revision') || t.includes('правок')) },
        ],
        showExclusive: false,
      };
    
    default:
      return {
        fields: [],
        showExclusive: false,
      };
  }
};

export function ItemDetailModal({ item, onClose, onLike, isLiked, onMessageClick }: ItemDetailModalProps) {
  const translations = t();
  const tg = getTelegramWebApp();
  const [isPlaying, setIsPlaying] = useState(false);
  const [localIsLiked, setLocalIsLiked] = useState(isLiked);
  const [showControls, setShowControls] = useState(false);
  const mediaRef = useRef<HTMLMediaElement | null>(null);
  const clickCountRef = useRef(0);
  const clickTimerRef = useRef<NodeJS.Timeout | null>(null);

  useTimeOnCard(item?.id, { eventType: 'view', minDurationMs: 1200 });

  const previewSource = item.preview_url || item.media_url;
  const mediaInfo = detectMediaPreview(previewSource);
  const productMetadata = getProductMetadata(item);

  // Auto-play on mount
  useEffect(() => {
    if (mediaInfo.type !== 'none' && mediaInfo.type !== 'direct-link') {
      setIsPlaying(true);
    }
  }, [mediaInfo.type]);

  useEffect(() => {
    setLocalIsLiked(isLiked);
  }, [isLiked]);

  // Control media playback
  useEffect(() => {
    const node = mediaRef.current;
    if (!node) return;

    if (isPlaying) {
      node.play().catch(() => {});
    } else {
      try {
        node.pause();
      } catch {
        // ignore
      }
    }
  }, [isPlaying]);

  // Cleanup
  useEffect(() => {
    return () => {
      const node = mediaRef.current;
      if (node) {
        try {
          node.pause();
        } catch {
          // ignore
        }
      }
    };
  }, []);

  const handleCoverClick = () => {
    // Double-click logic
    clickCountRef.current += 1;

    if (clickTimerRef.current) {
      clearTimeout(clickTimerRef.current);
    }

    if (clickCountRef.current === 1) {
      // First click - show controls
      clickTimerRef.current = setTimeout(() => {
        setShowControls(true);
        clickCountRef.current = 0;
      }, 300);
    } else if (clickCountRef.current === 2) {
      // Second click - toggle play/pause
      clickCountRef.current = 0;
      if (clickTimerRef.current) {
        clearTimeout(clickTimerRef.current);
      }
      
      if (mediaInfo.type !== 'none' && mediaInfo.type !== 'direct-link') {
        haptic.medium();
        setShowControls(true);
        setIsPlaying(!isPlaying);
      }
    }
  };

  const getDeliverableFiles = () => {
    const rawFormats = (item as { formats?: unknown }).formats;
    if (!Array.isArray(rawFormats)) return [];

    const files = rawFormats
      .filter((entry) => entry && typeof entry === 'object')
      .filter((entry) => {
        const role = String(entry.role ?? '').toLowerCase();
        return role === 'deliverable';
      })
      .map((entry) => {
        const url = String(entry.url || '');
        const name = (entry.name as string | undefined) ?? '';
        const type = (entry.type as string | undefined) ?? '';
        return { url, name, type };
      })
      .filter((file) => !!file.url);

    return files;
  };

  const triggerDownload = (url: string, filename?: string) => {
    try {
      const link = document.createElement('a');
      link.href = url;
      if (filename) {
        link.download = filename;
      }
      link.rel = 'noopener noreferrer';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };

  const handleBuy = () => {
    haptic.medium();

    const deliverables = getDeliverableFiles();
    if (!deliverables.length) {
      tg?.showAlert?.('Для цього товару ще не додано файлів для завантаження.');
      return;
    }

    const primaryFile = deliverables[0];
    triggerDownload(primaryFile.url, primaryFile.name || undefined);
  };
  
  const handleMessage = () => {
    haptic.light();
    if (onMessageClick && item.creator_id) {
      // Use the provided callback to navigate to chats
      onMessageClick(item.creator_id, item.creator?.username, item);
    } else if (item.creator?.username) {
      // Fallback to opening Telegram link
      tg?.openTelegramLink(`https://t.me/${item.creator.username}`);
    }
  };

  const handleShare = () => {
    const url = `https://t.me/splyt_bot/market?startapp=item_${item.id}`;
    const text = `Check out "${item.title}" by ${item.creator?.name || 'this creator'} on Splyt!`;
    const telegramUrl = `https://t.me/share/url?url=${encodeURIComponent(url)}&text=${encodeURIComponent(text)}`;

    // Direct share - no delays
    if (tg?.openTelegramLink) {
      tg.openTelegramLink(telegramUrl);
    } else {
      window.open(telegramUrl, '_blank', 'noopener,noreferrer');
    }
  };

  const handleLike = async () => {
    // Optimistic update
    setLocalIsLiked(!localIsLiked);
    haptic.light();

    try {
      // Call parent onLike for analytics or other side effects
      if (onLike) {
        onLike();
      }

      const response = await api.user.favorite(item.id);

      // Update local state based on backend response
      if (typeof response.favorited === 'boolean') {
        setLocalIsLiked(response.favorited);
      }
    } catch (error) {
      // Revert on error
      setLocalIsLiked(localIsLiked);
      console.error("Failed to like item:", error);
    }
  };

  const renderMedia = () => {
    if (!isPlaying) return null;

    switch (mediaInfo.type) {
      case 'youtube':
      case 'spotify':
      case 'soundcloud':
        if (!mediaInfo.embedUrl) return null;
        return (
          <div className="mb-4">
            <div className="rounded-2xl overflow-hidden border border-white/10 bg-black/40">
              <iframe
                src={mediaInfo.embedUrl}
                className="w-full"
                style={{ minHeight: mediaInfo.type === 'spotify' ? 180 : 200 }}
                allow="autoplay; clipboard-write; encrypted-media; picture-in-picture"
                allowFullScreen
              />
            </div>
          </div>
        );
      case 'video':
        if (!mediaInfo.embedUrl) return null;
        return (
          <video
            ref={(node) => { mediaRef.current = node; }}
            src={mediaInfo.embedUrl}
            className="w-full h-full absolute inset-0 object-cover"
            autoPlay
            loop
            playsInline
            muted={false}
          />
        );
      case 'audio':
        if (!mediaInfo.embedUrl) return null;
        return (
          <audio
            ref={(node) => { mediaRef.current = node; }}
            src={mediaInfo.embedUrl}
            autoPlay
            loop
          />
        );
      default:
        return null;
    }
  };

  const hasMedia = mediaInfo.type !== 'none' && mediaInfo.type !== 'direct-link';
  const isVideoOrAudio = mediaInfo.type === 'video' || mediaInfo.type === 'audio';
  
  return (
    <div 
      className="fixed inset-0 z-50 flex items-end justify-center bg-black/60 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-t-3xl glass backdrop-blur-2xl border-t border-white/10 animate-slide-up"
        onClick={(e) => e.stopPropagation()}
        style={{
          boxShadow: '0 -8px 32px rgba(0, 0, 0, 0.3)',
        }}
      >
        {/* Header */}
        <div className="sticky top-0 z-10 flex items-center justify-between p-4 glass backdrop-blur-2xl border-b border-white/10">
          <h3 className="text-xl font-bold text-white">{translations.itemDetail.description}</h3>
          <button
            onClick={onClose}
            className="w-10 h-10 rounded-full flex items-center justify-center glass border border-white/20 hover:bg-white/10 transition-all"
          >
            <X className="w-5 h-5 text-white" />
          </button>
        </div>
        
        {/* Content */}
        <div className="p-6">
          {/* Cover */}
          <div 
            className="relative aspect-video rounded-2xl overflow-hidden mb-6 cursor-pointer"
            onClick={handleCoverClick}
          >
            {/* Background media for video/audio */}
            {isVideoOrAudio && renderMedia()}
            
            <img
              src={item.cover_url}
              alt={item.title}
              className="w-full h-full object-cover"
              style={isPlaying && mediaInfo.type === 'video' ? { opacity: 0 } : {}}
            />
            
            {/* Play/Pause Button */}
            {hasMedia && showControls && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  haptic.medium();
                  setIsPlaying(!isPlaying);
                }}
                className="absolute inset-0 flex items-center justify-center bg-black/40 hover:bg-black/50 transition-all"
              >
                <div className="w-16 h-16 rounded-full bg-splyt-green flex items-center justify-center glow-green">
                  {isPlaying ? (
                    <Pause className="w-8 h-8 text-splyt-dark" fill="currentColor" />
                  ) : (
                    <Play className="w-8 h-8 text-splyt-dark ml-1" fill="currentColor" />
                  )}
                </div>
              </button>
            )}

            {/* Hidden controls indicator */}
            {hasMedia && !showControls && (
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none">
                {isPlaying ? (
                  <div className="w-16 h-16 rounded-full bg-black/40 backdrop-blur-xl flex items-center justify-center opacity-0 animate-pulse">
                    <Pause className="w-8 h-8 text-white" fill="currentColor" />
                  </div>
                ) : (
                  <div className="w-16 h-16 rounded-full bg-splyt-green/80 backdrop-blur-xl flex items-center justify-center glow-green">
                    <Play className="w-8 h-8 text-splyt-dark ml-1" fill="currentColor" />
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Embedded media (YouTube, Spotify, SoundCloud) */}
          {!isVideoOrAudio && renderMedia()}
          
          {/* Title & Creator */}
          <h2 className="text-2xl font-bold text-white mb-2">{item.title}</h2>
          <p className="text-white/70 mb-4">{item.creator?.name || 'Creator'}</p>
          
          {/* Description */}
          {item.description && (
            <div className="mb-6">
              <h4 className="text-sm font-semibold text-white/50 mb-2 uppercase">
                {translations.itemDetail.description}
              </h4>
              <p className="text-white/80">{item.description}</p>
            </div>
          )}
          
          {/* Product-Specific Metadata */}
          {productMetadata.fields.some(f => f.value) && (
            <div className="flex flex-wrap gap-2 mb-6">
              {productMetadata.fields.map((field) => {
                if (!field.value) return null;
                return (
                  <div key={field.key} className="px-4 py-2 rounded-xl glass border border-white/10">
                    <span className="text-sm text-white/70">{field.label}:</span>{' '}
                    <span className="text-white font-semibold">{field.value}</span>
                  </div>
                );
              })}
              {productMetadata.showExclusive && item.is_exclusive && (
                <div className="px-4 py-2 rounded-xl bg-splyt-purple/20 border border-splyt-purple/30">
                  <span className="text-splyt-purple font-semibold">{translations.itemDetail.exclusive}</span>
                </div>
              )}
            </div>
          )}
          
          {/* Tags */}
          {item.tags && item.tags.length > 0 && (
            <div className="mb-6">
              <h4 className="text-sm font-semibold text-white/50 mb-2 uppercase">
                {translations.itemDetail.tags}
              </h4>
              <div className="flex flex-wrap gap-2">
                {item.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-3 py-1 rounded-lg text-sm glass border border-white/10 text-white/70"
                  >
                    #{tag}
                  </span>
                ))}
              </div>
            </div>
          )}
          
          {/* Price & Actions */}
          <div className="sticky bottom-0 left-0 right-0 p-6 -mx-6 -mb-6 glass backdrop-blur-2xl border-t border-white/10">
            <div className="flex items-center gap-3 mb-4">
              <button
                onClick={handleLike}
                className="w-14 h-14 rounded-full flex items-center justify-center glass border border-white/20 hover:scale-110 transition-all"
              >
                <Heart
                  className={`w-6 h-6 ${localIsLiked ? 'fill-splyt-green text-splyt-green' : 'text-white'}`}
                  strokeWidth={2}
                />
              </button>
              
              <button
                onClick={handleMessage}
                className="w-14 h-14 rounded-full flex items-center justify-center glass border border-white/20 hover:scale-110 transition-all"
              >
                <MessageCircle className="w-6 h-6 text-white" />
              </button>
              
              <button
                onClick={handleShare}
                className="w-14 h-14 rounded-full flex items-center justify-center glass border border-white/20 hover:scale-110 transition-all"
              >
                <Share2 className="w-6 h-6 text-white" />
              </button>
              
              <button
                onClick={handleBuy}
                className="flex-1 h-14 rounded-2xl bg-splyt-green flex items-center justify-center gap-2 font-bold text-splyt-dark glow-green hover:scale-105 transition-all"
              >
                <span>{translations.purchase.buyNow}</span>
                <span>{formatPrice(item.price_uah / 100)}</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
